import React, { useContext, useState, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import StoreFormContainer from "components/store-form-container";
import { getQueryParams } from "lib/utils";
import { useTranslation } from "react-i18next";
import { Header } from "components/header";
import { useTakebackById } from "../../api/use-takebacks";
import AuthContext from "contexts/AuthContext";
import { useGenerateShipping } from "api/use-shipping";

export default function DropoffPrepareShipment() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const location = useLocation();
  const queryParams = getQueryParams(location.search);
  const takeback = useTakebackById({ id: queryParams.id });
  const { authTokens } = useContext(AuthContext);
  const [isGeneratingLabel, setIsGeneratingLabel] = useState(false);

  // Extract shipping label data from GraphQL structure
  const shippingLabel =
    takeback?.data?.u_ups_shipping_label_encoded ||
    takeback?.data?.u_lego_dhl_label_b64_encoded;
  const originalLabelUrl =
    takeback?.data?.u_ups_shipping_label_url ||
    takeback?.shipping_metadatas?.items?.find(
      (item) => item.key === "label_url"
    )?.value;

  // Set up shipping generation hook
  const generateShipping = useGenerateShipping({
    onSuccess: (data) => {
      setIsGeneratingLabel(false);
      // Extract the label URL from the response and open it immediately
      const shippingData = data?.data?.[0];
      const originalLabelUrl = shippingData?.shipping_metadatas?.items?.find(
        (item) => item.key === "label_url"
      )?.value;

      if (originalLabelUrl && queryParams.id) {
        try {
          const url = new URL(originalLabelUrl);
          const returnID = url.searchParams.get("returnID");

          if (returnID && authTokens?.access_token) {
            const labelUrl = `${window.location.origin}/api/pdf/shipping-label-no-instructions?returnID=${returnID}&token=${authTokens.access_token}`;
            // Open PDF in new tab for printing
            window.open(labelUrl, "_blank");
          } else {
            window.open(originalLabelUrl, "_blank");
          }
        } catch (error) {
          console.error("Error constructing label URL:", error);
          window.open(originalLabelUrl, "_blank");
        }
      }
    },
    onError: () => {
      setIsGeneratingLabel(false);
      window.alert("Failed to generate shipping label");
    },
  });

  // Construct label URL
  const labelUrl = useMemo(() => {
    if (originalLabelUrl && queryParams.id) {
      try {
        const url = new URL(originalLabelUrl);
        const returnID = url.searchParams.get("returnID");

        if (returnID && authTokens?.access_token) {
          return `${window.location.origin}/api/pdf/shipping-label-no-instructions?returnID=${returnID}&token=${authTokens.access_token}`;
        }
        return originalLabelUrl;
      } catch (error) {
        console.error("Error constructing label URL:", error);
        return originalLabelUrl;
      }
    }
    return originalLabelUrl;
  }, [originalLabelUrl, queryParams.id, authTokens.access_token]);

  const handleSubmit = () => {
    // Check if we have a shipping label to print
    const shippingPresent = !!(shippingLabel || originalLabelUrl);

    if (shippingPresent && labelUrl) {
      // Open PDF in new tab for printing
      window.open(labelUrl, "_blank");
    } else {
      // No shipping label available - generate one
      if (isGeneratingLabel) return; // Prevent double-clicks

      // Extract customer ID and takeback ID for shipping generation
      const customerId = takeback?.consumer.value;
      const takebackId = takeback?.number;

      if (!customerId || !takebackId) {
        window.alert(
          "Missing seller or takeback information required for shipping label generation"
        );
        return;
      }

      setIsGeneratingLabel(true);
      generateShipping.mutate({
        takeback_id: takebackId,
        customer_id: customerId,
        type: "MAIL_LABEL", // Generate printable shipping label
      });
    }
  };

  const handleDone = () => {
    navigate(t("urls.store_check_in"));
  };

  if (!takeback) {
    return null;
  }

  return (
    <StoreFormContainer
      submitLabel={
        isGeneratingLabel ? "Generating Label..." : "Print Shipping Label"
      }
      secondaryLabel="Done"
      onSecondary={handleDone}
      onSubmit={handleSubmit}
      disabled={isGeneratingLabel}
    >
      <Header />
      <div className="flex flex-col flex-grow py-4">
        <div className="flex flex-col items-center">
          <h2 className="font-semibold text-4xl text-center mb-6">
            Prepare package for shipment
          </h2>

          <p className="text-xl text-gray-600 text-center mb-8">
            The package can now be resealed, so it's ready for shipment
          </p>

          <div className="w-full max-w-2xl space-y-6 mb-8">
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0 w-8 h-8 bg-red text-white rounded-full flex items-center justify-center font-bold">
                1
              </div>
              <p className="text-lg">Seal the box to secure the contents.</p>
            </div>

            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0 w-8 h-8 bg-red text-white rounded-full flex items-center justify-center font-bold">
                2
              </div>
              <p className="text-lg">
                Print and attach the shipping label and set aside for UPS
                pickup.
              </p>
            </div>
          </div>

          <div className="bg-gray-100 p-4 rounded-lg">
            <p className="text-lg font-semibold">
              Keep this case number handy: {takeback.number}
            </p>
          </div>
        </div>
      </div>
    </StoreFormContainer>
  );
}
